import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2 } from 'lucide-react';
import { initializePaddle } from '@paddle/paddle-js';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabaseClient';
import './Pricing.css';

const CLIENT_TOKEN = process.env.REACT_APP_PADDLE_CLIENT_TOKEN || 'test_b539b44a61f86fe36a9fe1f99c5';
const PADDLE_ENV = process.env.REACT_APP_PADDLE_ENV || 'sandbox';

const defaultPlans = [
    {
        name: 'Basic',
        price: '$0',
        period: '/month',
        description: 'Perfect for exploring AI-powered architectural design.',
        priceId: null,
        productId: null,
        features: [
            'Bring Your Own API Key (BYOK) supported',
            '1 device login limit',
            'No image generation',
            'Basic chat & community support',
        ],
        cta: 'Get Started',
        featured: false,
    },
    {
        name: 'Pro',
        price: '$19',
        period: '/month',
        description: 'For growing professionals who need more power and credits.',
        priceId: 'pri_01m0wa8rpw2whyc8egr5b8bfys',
        productId: 'pro_01m0wa8re2f3kkcpebn99cbts3',
        features: [
            '50,000 tokens per month included',
            'Full Managed API Proxy usage',
            '2 device login limit',
            'Image generation & Priority support',
        ],
        cta: 'Upgrade to Pro',
        featured: true,
    },
    {
        name: 'Studio',
        price: '$50',
        period: '/month',
        description: 'For teams and shops requiring higher volumes and collaboration.',
        priceId: 'pri_01m0wa8s6h0699e2874qvaavqw',
        productId: 'pro_01m0wa8rz84atwrjck8fq2v243',
        features: [
            '150,000 tokens per month included',
            'Full Managed API Proxy usage',
            '5 device login limit',
            'Extended image gen & Team features',
        ],
        cta: 'Get Studio',
        featured: false,
    },
    {
        name: 'Enterprise',
        price: 'Custom',
        period: '',
        description: 'Tailored solutions for large teams and organizations.',
        priceId: 'pri_01m0wa8sqgkkvtbzce023xhera',
        productId: 'pro_01m0wa8sf7zjf7kz9j8gwbc8jt',
        features: [
            '1,000,000 tokens per month included',
            '10+ seats & device limit',
            'Dedicated team dashboard & SLA',
            'Custom integrations & On-premise',
        ],
        cta: 'Contact Sales',
        featured: false,
    },
];

export default function Pricing() {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [paddle, setPaddle] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(null);
    const [plans, setPlans] = useState(defaultPlans);

    // Dynamic fetching of plan prices from Supabase app_settings table
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const { data, error } = await supabase
                    .from('app_settings')
                    .select('*');

                if (!error && data && data.length > 0) {
                    // Filter out duplicate 'free' plan if 'basic' exists
                    const filtered = data.filter(s => {
                        const pName = s.plan_name?.toLowerCase();
                        if (pName === 'free' && data.some(d => d.plan_name?.toLowerCase() === 'basic')) {
                            return false;
                        }
                        return true;
                    });

                    const mapped = filtered.map((setting) => {
                        const cost = Number(setting.subscription_cost || 0);
                        const proxyCredit = Number((cost * 0.65).toFixed(2));
                        const isFree = cost === 0 || setting.plan_name?.toLowerCase() === 'free' || setting.plan_name?.toLowerCase() === 'basic';
                        const planNameCap = setting.plan_name ? setting.plan_name.charAt(0).toUpperCase() + setting.plan_name.slice(1) : 'Plan';

                        const isSandbox = PADDLE_ENV === 'sandbox';
                        const fallbackPriceId = isSandbox ? (
                            setting.plan_name === 'pro' ? 'pri_01m0wa8rpw2whyc8egr5b8bfys' : 
                            setting.plan_name === 'studio' ? 'pri_01m0wa8s6h0699e2874qvaavqw' : 
                            setting.plan_name === 'enterprise' ? 'pri_01m0wa8sqgkkvtbzce023xhera' : null
                        ) : null;

                        return {
                            name: planNameCap,
                            price: isFree ? '$0' : `$${cost}`,
                            period: isFree ? '/month' : '/month',
                            description: isFree
                                ? 'Use your own API Key (BYOK) with full access & no proxy limits.'
                                : `For growing professionals needing power and credits.`,
                            priceId: setting.paddle_price_id || fallbackPriceId,
                            productId: setting.paddle_product_id,
                            proxyCredit: proxyCredit,
                            features: [
                                isFree ? 'Bring Your Own API Key (BYOK) supported' : 'Managed API Proxy Included',
                                `${setting.device_limit || 1} device login limit`,
                                setting.token_limit ? `${setting.token_limit.toLocaleString()} tokens per month` : 'All AI models included',
                                isFree ? 'Basic chat & community support' : 'Priority support & Image generation',
                            ],
                            cta: isFree ? 'Get Started' : `Upgrade to ${planNameCap}`,
                            featured: setting.plan_name?.toLowerCase() === 'pro',
                        };
                    });

                    const order = ['Basic', 'Pro', 'Studio', 'Enterprise'];
                    mapped.sort((a, b) => order.indexOf(a.name) - order.indexOf(b.name));
                    setPlans(mapped);
                }
            } catch (err) {
                console.error('Error loading dynamic plans from Supabase:', err);
            }
        };

        fetchSettings();
    }, []);

    // Sync checkout completion directly into Supabase DB using 65% rule
    const syncSubscriptionToDatabase = async (planName, priceAmount) => {
        if (!user) return;
        try {
            const planTier = (planName || 'pro').toLowerCase();
            const cost = typeof priceAmount === 'number' ? priceAmount : parseFloat(priceAmount) || 19;
            const tokenLimit = planTier === 'studio' ? 150000 : planTier === 'enterprise' ? 500000 : 50000;
            const apiCallsLimitUsd = Number((cost * 0.65).toFixed(2)); // 65% Allowance Rule

            // 1. Update user profile tier and token balance
            await supabase
                .from('profiles')
                .update({
                    subscription_tier: planTier,
                    token_balance: tokenLimit,
                    updated_at: new Date().toISOString(),
                })
                .eq('id', user.id);

            // 2. Upsert active subscription record in database
            await supabase
                .from('subscriptions')
                .upsert({
                    user_id: user.id,
                    plan_type: planTier,
                    status: 'active',
                    captured_cost: cost,
                    captured_duration_days: 30,
                    api_calls_limit_usd: apiCallsLimitUsd,
                    start_date: new Date().toISOString(),
                });

            console.log(`Successfully saved active subscription into Supabase database with 65% API limit ($${apiCallsLimitUsd})!`);
        } catch (dbErr) {
            console.error('Error syncing subscription to database:', dbErr);
        }
    };

    useEffect(() => {
        if (CLIENT_TOKEN) {
            initializePaddle({
                token: CLIENT_TOKEN,
                environment: PADDLE_ENV,
                eventCallback: (event) => {
                    if (event.name === 'checkout.completed') {
                        const customData = event.data?.custom_data;
                        const planName = customData?.plan_name || 'pro';
                        syncSubscriptionToDatabase(planName, 19);
                        navigate('/dashboard?payment=success');
                    }
                },
            }).then((paddleInstance) => {
                if (paddleInstance) {
                    setPaddle(paddleInstance);
                }
            }).catch((err) => {
                console.error('Failed to initialize Paddle:', err);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Resume pending checkout if user logged in after clicking Upgrade
    useEffect(() => {
        if (user && paddle) {
            const pendingPriceId = sessionStorage.getItem('pending_checkout_price_id');
            const pendingPlanName = sessionStorage.getItem('pending_checkout_plan_name');

            if (pendingPriceId) {
                sessionStorage.removeItem('pending_checkout_price_id');
                sessionStorage.removeItem('pending_checkout_plan_name');
                openPaddleCheckout(pendingPriceId, pendingPlanName || 'Pro');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user, paddle]);

    const openPaddleCheckout = (priceId, planName) => {
        if (!paddle) return;
        setLoadingPlan(planName);
        try {
            const checkoutOptions = {
                items: [{ priceId, quantity: 1 }],
                settings: {
                    displayMode: 'overlay',
                    theme: 'dark',
                    locale: 'en',
                },
            };

            if (user?.email) {
                checkoutOptions.customer = { email: user.email };
            }

            if (user?.id || planName) {
                checkoutOptions.customData = {
                    user_id: user?.id || '',
                    plan_name: (planName || 'pro').toLowerCase(),
                };
            }

            paddle.Checkout.open(checkoutOptions);
        } catch (err) {
            console.error('Error opening Paddle checkout:', err);
        } finally {
            setLoadingPlan(null);
        }
    };

    const handlePlanClick = (plan) => {
        if (plan.name === 'Enterprise' || plan.cta === 'Contact Sales') {
            window.location.href = 'mailto:revitroneapp@gmail.com';
            return;
        }

        if (!plan.priceId) {
            if (plan.name === 'Basic' || plan.name === 'Free') {
                if (!user) {
                    navigate('/signup');
                } else {
                    alert('You are currently on the Basic plan.');
                }
            }
            return;
        }

        // If user is not logged in -> save plan & take user to login page
        if (!user) {
            sessionStorage.setItem('pending_checkout_price_id', plan.priceId);
            sessionStorage.setItem('pending_checkout_plan_name', plan.name);
            navigate('/login');
            return;
        }

        // If user is logged in -> launch Paddle overlay checkout
        openPaddleCheckout(plan.priceId, plan.name);
    };

    return (
        <section id="pricing" className="pricing">
            <div className="pricing__container">
                <div className="pricing__header">
                    <h2 className="pricing__kicker">Pricing</h2>
                    <h3 className="pricing__title">Simple, transparent pricing</h3>
                    <p className="pricing__subtitle">
                        Choose the plan that fits your workflow. No hidden fees, cancel anytime.
                    </p>
                </div>

                <div className="pricing__grid">
                    {plans.map((plan) => (
                        <div
                            key={plan.name}
                            className={`pricing__card ${plan.featured ? 'pricing__card--featured' : ''}`}
                        >
                            {plan.featured && <div className="pricing__badge">Most Popular</div>}
                            <h4 className="pricing__planName">{plan.name}</h4>
                            <div className="pricing__priceRow">
                                <span className="pricing__price">{plan.price}</span>
                                {plan.period && <span className="pricing__period">{plan.period}</span>}
                            </div>
                            <p className="pricing__planDesc">{plan.description}</p>

                            <ul className="pricing__features">
                                {plan.features.map((feature) => (
                                    <li key={feature} className="pricing__feature">
                                        <Check className="pricing__checkIcon" aria-hidden="true" />
                                        <span>{feature}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                className={`pricing__cta ${plan.featured ? 'pricing__cta--primary' : ''}`}
                                type="button"
                                onClick={() => handlePlanClick(plan)}
                                disabled={loadingPlan === plan.name}
                            >
                                {loadingPlan === plan.name ? (
                                    <span className="flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin inline" /> Loading...
                                    </span>
                                ) : (
                                    plan.cta
                                )}
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}

